import * as CONST from '../Constants.js';
import '../App.css';
import LatexFormula from '../components/LatexFormula.js';

const latex = String.raw;

const FORMULAS = {
  de: {
    crClMale: latex`CrCl_{\mathrm{Mann}} = \frac{(140 - \mathrm{Alter}) \cdot \mathrm{Gewicht}}{72 \cdot \mathrm{Serumkreatinin}}`,
    crClFemale: latex`CrCl_{\mathrm{Frau}} = 0{,}85 \cdot CrCl_{\mathrm{Mann}}`,
    capQ: latex`Q = Q_0 + \frac{eGFR}{100\ \mathrm{mL/min}} \cdot (1 - Q_0)`,
    clearance: latex`Cl = Q \cdot Cl_{pop}`,
    eliminationConstant: latex`k_e = -\frac{\ln c_{Tal} - \ln(c_{min} + \frac{\mathrm{Dosis}}{V_D})}{\Delta t}`,
    measuredClearance: latex`Cl = k_e \cdot V_D`,
    dailyDose: latex`\mathrm{Dosis}_{24h} = AUC \cdot Cl`,
    cmaxSteadyState: latex`C_{max,ss} = \frac{\mathrm{Dosis}}{V_D \cdot k_e \cdot t_{inf}} \cdot \frac{1 - e^{-k_e \cdot t_{inf}}}{1 - e^{-k_e \cdot T}}`,
    cminSteadyState: latex`C_{min,ss} = C_{max,ss} \cdot e^{-k_e \cdot (T - t_{inf})}`,
  },
  en: {
    crClMale: latex`CrCl_{\mathrm{male}} = \frac{(140 - \mathrm{age}) \cdot \mathrm{weight}}{72 \cdot \mathrm{serum\ creatinine}}`,
    crClFemale: latex`CrCl_{\mathrm{female}} = 0.85 \cdot CrCl_{\mathrm{male}}`,
    capQ: latex`Q = Q_0 + \frac{eGFR}{100\ \mathrm{mL/min}} \cdot (1 - Q_0)`,
    clearance: latex`Cl = Q \cdot Cl_{pop}`,
    eliminationConstant: latex`k_e = -\frac{\ln c_{trough} - \ln(c_{min} + \frac{\mathrm{Dose}}{V_D})}{\Delta t}`,
    measuredClearance: latex`Cl = k_e \cdot V_D`,
    dailyDose: latex`\mathrm{Dose}_{24h} = AUC \cdot Cl`,
    cmaxSteadyState: latex`C_{max,ss} = \frac{\mathrm{Dose}}{V_D \cdot k_e \cdot t_{inf}} \cdot \frac{1 - e^{-k_e \cdot t_{inf}}}{1 - e^{-k_e \cdot T}}`,
    cminSteadyState: latex`C_{min,ss} = C_{max,ss} \cdot e^{-k_e \cdot (T - t_{inf})}`,
  },
};

const contentStyle = {
  width: '90%',
  maxWidth: 900,
  color: 'white',
  textAlign: 'left',
};

const FormulaBlock = ({ source }) => (
  <span style={{
    display: 'block',
    margin: '10px 0',
    padding: '10px 12px',
    backgroundColor: 'rgba(255, 255, 255, 0.08)',
    fontSize: CONST.SCHRIFTMITTEL,
    lineHeight: 1.45,
    overflowX: 'auto',
    whiteSpace: 'nowrap',
  }}>
    <LatexFormula source={source} />
  </span>
);

export default function HinweiseScreen({ language }) {
  const formulas = FORMULAS[language ? 'en' : 'de'];

  return (
    <div style={{
      display: 'flex',
      width: '100%',
      flexDirection: 'column',
      margin: 0,
      alignItems: 'center',
      fontFamily: 'Inter',
      fontSize: CONST.SCHRIFTGROSS,
      color: 'white',
      backgroundColor: 'transparent',
    }}>
      <h2>{language ? 'About MeroEasy' : 'Über MeroEasy'}</h2>

      {language && (
        <div style={contentStyle}>
          <p>
            MeroEasy is a training software developed for physicians and pharmacists to estimate influences of meropenem regimens and to
            understand pharmacokinetic formulas. This project was supported by the ADKA e.V. MeroEasy uses pharmacokinetic population
            estimates, the Dettli [1,2] and the Sawchuk-Zaske methods [1] to calculate meropenem dosing regimens.
          </p>
          <p>
            Meropenem regimens can be calculated both empirically, without any prior doses, or using meropenem levels in steady state.
            MeroEasy determines pharmacokinetic parameters and meropenem dosing strategies using the following steps:
          </p>

          <h4>1. Estimate Cl and Vd</h4>
          <p>
            Empiric dosing: Clearance (CL) and volume of distribution (Vd) are determined using population estimates from published
            literature (Vd = 0.25 L/kg, CLpop = 13 L/h) [3]. Vd is assumed considering ideal body weight plus 40% of the body mass above
            ideal body weight. If actual body weight is less than ideal body weight, actual body weight is used.
          </p>
          <p>
            Creatinine clearance is calculated using the Cockcroft Gault equation [1,4] with a 40% adjustment factor, or actual body
            weight if actual body weight is below ideal body weight. Serum creatinine is cut off at 0.6 in women and 0.7 in men to prevent
            overestimation in cachectic patients:
          </p>
          <FormulaBlock source={formulas.crClMale} />
          <FormulaBlock source={formulas.crClFemale} />

          <p>
            The dose adjustment factor Q is determined by the ratio of the patient’s eGFR to a presumed normal eGFR of 100 ml/min,
            based on estimation of the minimal elimination rate fraction in subjects with normal renal function (Q0 = 0.25) [2,3]:
          </p>
          <FormulaBlock source={formulas.capQ} />
          <FormulaBlock source={formulas.clearance} />

          <p>
            If one trough level is available, Vd is assumed using ideal body weight plus 40% of excess body mass, or actual body weight
            when actual body weight is lower. The measured serum concentration is assigned to the corresponding half-life as a function
            of infusion duration, and CL is then calculated from these parameters.
          </p>
          <FormulaBlock source={formulas.eliminationConstant} />
          <FormulaBlock source={formulas.measuredClearance} />

          <h4>2. Loading dose</h4>
          <p>
            A loading dose should be considered to achieve rapid attainment of effective serum concentrations. For continuous infusion,
            500 mg as short infusion followed by immediate continuous infusion is recommended; for intermittent infusion, 1000 mg as
            short infusion is recommended [5,6].
          </p>

          <h4>3. One-compartment PK equations</h4>
          <p>
            Once Cl, Vd, and ke are determined, one-compartment pharmacokinetic equations are used to estimate trough level (cmin,ss),
            peak level (cmax,ss), and the required daily dose for a dosing strategy, taking dose, infusion time (tinf), and dosing
            interval (T) into account:
          </p>
          <FormulaBlock source={formulas.dailyDose} />
          <FormulaBlock source={formulas.cmaxSteadyState} />
          <FormulaBlock source={formulas.cminSteadyState} />

          <h4>Target range for meropenem concentration</h4>
          <p>For continuous infusion: 4-6(8) x MIC [7]</p>
          <p>For intermittent infusion: 1-2(4) x MIC [8]</p>
          <p>
            MIC targets are based on MIC breakpoints from the European Committee on Antimicrobial Susceptibility Testing (EUCAST) [9].
            All calculations assume MIC = 2 mg/L [9], but can be adjusted by entering the MIC. For infections with intermediate
            susceptible pathogens or CNS infections, doubling the target range is recommended: c/MIC = 8-12 with continuous infusion and
            cmin/MIC = 2-4 with intermittent infusion [10].
          </p>
          <p>
            “Dosing outside of target range” may appear for intermittent infusion if the computed dose has a cmin/MIC below 1 or above 4,
            and for continuous infusion if the computed dose has a c/MIC below 8 or above 16. The calculation of a “New Dose” is
            user-controlled and has no dose or infusion-duration limitations. For continuous infusion, maintenance doses are limited to
            at least 250 mg/dose and at most 16000 mg/dose. For intermittent infusion, maintenance doses are limited to at least
            250 mg/dose and at most 3000 mg/dose. The minimum dosing interval is q4h.
          </p>

          <h4>Inappropriate populations for this calculator</h4>
          <p>
            Renal replacement therapy (see dosing www.thecaddy.de instead), unstable renal function, Meropenem MIC {'≥'} 8 mg/L,
            pediatrics ({'<'} 18 years), cystic fibrosis, severe burn injury.
          </p>

          <h4>Disclaimer</h4>
          <p>
            MeroEasy has been created for personal use only. The use of any result generated by MeroEasy is in any case the sole risk
            and responsibility of the meropenem calculator user. Therapeutic decisions should not solely rely on MeroEasy, as information
            provided by MeroEasy does not replace clinical judgement. Users should seek a doctor’s advice in addition to using this app
            and before making any medical decisions. Although MeroEasy has been validated carefully, there is no guarantee for the
            accuracy of the provided results. This app is provided by Dr Ute Chiriac, Heidelberg University Hospital & Dr Otto Frey,
            Heidenheim Hospital.
          </p>

          <h4>Literature</h4>
          <p>[1] Murphy JE. Clinical pharmacokinetics. 7th Ed. American Society of Health Pharmacists (ASHP), 2021.</p>
          <p>[2] Dettli L. The kidney in pre-clinical and clinical pharmacokinetics. Jpn J Clin Pharmacol Ther 1984;15: 241-54.</p>
          <p>[3] Summary of product characteristics Meronem®, Germany: May 2021.</p>
          <p>[4] Cockcroft DW, Gault MH. Prediction of creatinine clearance from serum creatinine. Nephron 1976;16: 31-41.</p>
          <p>[5] Liebchen U, Salletmeier H, Kallee S et al. Optimal loading dose of meropenem before continuous infusion in critically ill patients: a simulation study. Scientific reports 2021;11(1):17211.</p>
          <p>[6] Resistenztestung und Antibiotika-Dosierung, Stand 05.09.2025: Available online at <a href="https://www.nak-deutschland.org" target="_blank" rel="noopener noreferrer">https://www.nak-deutschland.org</a></p>
          <p>[7] Gatti M, Cojutti PG, Bartoletti M et al. Expert clinical pharmacological advice may make an antimicrobial TDM program for emerging candidates more clinically useful in tailoring therapy of critically ill patients. Crit Care 2022;26(1):178.</p>
          <p>[8] Scharf C, Liebchen U, Paal M et al. The higher the better? Defining the optimal beta-lactam target for critically ill patients to reach infection resolution and improve outcome. J Intensive Care 2020;8(1):86.</p>
          <p>[9] EUCAST. Antimicrobial wild type distributions of microorganisms for Meropenem. Available online at mic.eucast.org.</p>
          <p>[10] Tiede C, Chiriac U, Dubinski D et al. Cerebrospinal Fluid Concentrations of Meropenem and Vancomycin in Ventriculitis Patients Obtained by TDM-Guided Continuous Infusion. Antibiotics 2021;10(11):1421.</p>
        </div>
      )}

      {!language && (
        <div style={contentStyle}>
          <p>
            MeroEasy ist eine Lernsoftware, entwickelt mit Unterstützung der ADKA e.V. für die Ausbildung von Ärzten und Apothekern,
            um Einflüsse der Meropenem-Dosierung abzuschätzen und pharmakokinetische Formeln zu verstehen. MeroEasy verwendet zur
            Berechnung der Meropenem-Dosierung pharmakokinetische Populationsschätzungen, die Dettli-Methode [1,2] und die
            Sawchuk-Zaske-Methode [1].
          </p>
          <p>
            Meropenem-Dosierungen können sowohl empirisch, also ohne vorherige Dosierung, als auch unter Verwendung eines
            Meropenem-Spiegels berechnet werden. MeroEasy ermittelt pharmakokinetische Parameter und Meropenem-Dosierungsstrategien
            anhand der folgenden Schritte:
          </p>

          <h4>1. Abschätzung von Cl und Vd</h4>
          <p>
            Empirische Dosierung: Meropenem-Clearance (Cl) und Verteilungsvolumen (Vd) werden über Populationsdaten aus der Literatur
            bestimmt (Vd = 0.25 L/kg, CLpop = 13 L/h) [3]. Vd wird unter Berücksichtigung des idealen Körpergewichts plus 40% des
            Übergewichts abgeschätzt. Bei einem aktuellen Körpergewicht unter dem idealen Körpergewicht wird das aktuelle Körpergewicht
            verwendet.
          </p>
          <p>
            Die Kreatinin-Clearance (CrCl) wird über Cockcroft Gault [1,4] mit dem idealen Körpergewicht plus 40% des Übergewichts
            berechnet bzw. mit dem aktuellen Körpergewicht, wenn dieses unter dem idealen Körpergewicht liegt. Der Serumkreatininwert
            wird bei Frauen bei 0,6 und bei Männern bei 0,7 begrenzt, um eine Überschätzung bei kachektischen Patienten zu verhindern:
          </p>
          <FormulaBlock source={formulas.crClMale} />
          <FormulaBlock source={formulas.crClFemale} />

          <p>
            Die Cl wird dann nach Dettli über die individuelle Ausscheidungskapazität Q (Q0 = 0.25) [2,3] berechnet:
          </p>
          <FormulaBlock source={formulas.capQ} />
          <FormulaBlock source={formulas.clearance} />

          <p>
            Ist ein Talspiegel vorhanden, wird Vd unter Berücksichtigung des idealen Körpergewichts plus 40% des Übergewichts
            abgeschätzt bzw. mit dem aktuellen Körpergewicht, wenn dieses unter dem idealen Körpergewicht liegt. Der gemessene
            Serumspiegel wird in Abhängigkeit von der Infusionsdauer der korrespondierenden Halbwertszeit zugeordnet; aus diesen
            Parametern wird anschließend die Cl berechnet.
          </p>
          <FormulaBlock source={formulas.eliminationConstant} />
          <FormulaBlock source={formulas.measuredClearance} />

          <h4>2. Initialdosis</h4>
          <p>
            Um ein schnelles Erreichen wirksamer Serumkonzentrationen zu erreichen, sollte eine Initialdosis in Betracht gezogen werden.
            Für die Dauerinfusion werden 500 mg, für die intermittierende Gabe 1000 mg als Kurzinfusion empfohlen [5,6].
          </p>

          <h4>3. Ein-Kompartiment-Modelle</h4>
          <p>
            Wenn Cl, Vd und ke bestimmt sind, lassen sich unter Berücksichtigung von Dosis, Infusionsdauer (tinf) und Dosisintervall
            (T) anhand der Formeln für 1-Kompartiment-Modelle Talspiegel (cmin,ss), Spitzenspiegel (cmax,ss) und die erforderliche
            Tagesdosis (Dosis24h) für eine Dosisstrategie abschätzen:
          </p>
          <FormulaBlock source={formulas.dailyDose} />
          <FormulaBlock source={formulas.cmaxSteadyState} />
          <FormulaBlock source={formulas.cminSteadyState} />

          <h4>Zielbereich für die Meropenem-Konzentration</h4>
          <p>- bei dauerhafter Gabe: 4-6(8) x MHK [7]</p>
          <p>- bei intermittierender Gabe: 1-2(4) x MHK [8]</p>
          <p>
            Die MHK-Ziele basieren auf den MHK-Breakpoints der European Committee on Antimicrobial Susceptibility Testing (EUCAST) [9].
            Alle Berechnungen gehen von MHK = 2 mg/L aus [9], können aber durch die Eingabe der MHK angepasst werden. Bei Infektionen
            mit i-getesteten Keimen oder ZNS-Infektionen wird eine Verdopplung der Zielwerte empfohlen: c/MHK = 8-12 bei Dauerinfusion
            und cmin/MHK = 2-4 bei intermittierender Gabe [10].
          </p>
          <p>
            „cmin/MHK nicht im angestrebten Bereich“ kann bei intermittierender Gabe erscheinen, wenn die berechnete Dosis eine
            cmin/MHK von weniger als 1 oder mehr als 4 aufweist. „c/MHK nicht im angestrebten Bereich“ kann bei Dauerinfusion erscheinen,
            wenn die berechnete Dosis eine c/MHK von weniger als 8 oder mehr als 16 aufweist. Die Berechnung einer „Neuen Dosierung“ ist
            benutzergesteuert und hat keine Dosis- oder Infusionsdauereinschränkungen. Die Erhaltungsdosen sind auf mindestens
            250 mg/Dosis und maximal 16000 mg/Dosis bei dauerhafter Gabe bzw. 3000 mg/Dosis bei intermittierender Gabe begrenzt. Das
            Dosierungsintervall bei intermittierender Gabe ist auf mindestens 4 h begrenzt.
          </p>

          <h4>Ungeeignete Patientenpopulationen für diese Berechnungen</h4>
          <p>
            Nierenersatzverfahren (Berechnung der Erhaltungsdosis mit www.thecaddy.de möglich), instabile Nierenfunktion,
            Meropenem-MHK {'≥'} 8 mg/l, Pädiatrie ({'<'} 18 Jahre), Mukoviszidose, schwere Verbrennungsverletzung.
          </p>

          <h4>Haftungsausschluss</h4>
          <p>
            MeroEasy wurde nur für den persönlichen Gebrauch erstellt. Die Verwendung eines von MeroEasy erzeugten Ergebnisses erfolgt
            in jedem Fall auf alleiniges Risiko und Verantwortung des MeroEasy-Benutzers. Therapeutische Entscheidungen sollten nicht
            ausschließlich auf MeroEasy beruhen, da die von MeroEasy bereitgestellten Informationen die klinische Beurteilung nicht
            ersetzen. MeroEasy-Benutzer sollten zusätzlich zur Verwendung dieser App und vor medizinischen Entscheidungen einen Arzt
            konsultieren. Obwohl MeroEasy sorgfältig validiert wurde, gibt es keine Garantie für die Richtigkeit der bereitgestellten
            Ergebnisse. Diese App wird bereitgestellt von Dr. Ute Chiriac, Universitätsklinikum Heidelberg & Dr. Otto Frey, Klinikum
            Heidenheim.
          </p>

          <h4>Literatur</h4>
          <p>[1] Murphy JE. Clinical pharmacokinetics. 7th Ed. American Society of Health Pharmacists (ASHP), 2021.</p>
          <p>[2] Dettli L. The kidney in pre-clinical and clinical pharmacokinetics. Jpn J Clin Pharmacol Ther 1984;15: 241-54.</p>
          <p>[3] Fachinformation Meronem®, Deutschland. Stand: Mai 2021.</p>
          <p>[4] Cockcroft DW, Gault MH. Prediction of creatinine clearance from serum creatinine. Nephron 1976;16: 31-41.</p>
          <p>[5] Liebchen U, Salletmeier H, Kallee S et al. Optimal loading dose of meropenem before continuous infusion in critically ill patients: a simulation study. Scientific reports 2021;11(1):17211.</p>
          <p>[6] Resistenztestung und Antibiotika-Dosierung, Stand 05.09.2025: Available online at <a href="https://www.nak-deutschland.org" target="_blank" rel="noopener noreferrer">https://www.nak-deutschland.org</a></p>
          <p>[7] Gatti M, Cojutti PG, Bartoletti M et al. Expert clinical pharmacological advice may make an antimicrobial TDM program for emerging candidates more clinically useful in tailoring therapy of critically ill patients. Crit Care 2022;26(1):178.</p>
          <p>[8] Scharf C, Liebchen U, Paal M et al. The higher the better? Defining the optimal beta-lactam target for critically ill patients to reach infection resolution and improve outcome. J Intensive Care 2020;8(1):86.</p>
          <p>[9] EUCAST. Antimicrobial wild type distributions of microorganisms for Meropenem. Available online at mic.eucast.org.</p>
          <p>[10] Tiede C, Chiriac U, Dubinski D et al. Cerebrospinal Fluid Concentrations of Meropenem and Vancomycin in Ventriculitis Patients Obtained by TDM-Guided Continuous Infusion. Antibiotics 2021;10(11):1421.</p>
        </div>
      )}
    </div>
  );
}
